import {
  ESessionPhase,
  IBoxCore,
  ISessionBodyPlayerPublic,
  ISessionCore,
  TBoxBodyPublic,
  TGamePublic,
} from '@lbs/shared-rp-types'
import { RussianPokerStore } from '../../stores/RussianPoker'

export { ESessionPhase }

export const JOIN_GAME_TIMEOUT_ERROR = 'JOIN_GAME_TIMEOUT'

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export enum ERpState {
  Disconnected = 'disconnected',
  Connected = 'connected',
  Joining = 'joining',
  Joined = 'joined',
  Leaving = 'leaving',
  Left = 'left',
  Error = 'error',
}

export enum ERpLeavingState {
  Normal = 'normal',
  Force = 'force',
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export enum ERpEvent {
  SocketConnected = 'SOCKET_CONNECTED',
  SocketDisconnected = 'SOCKET_DISCONNECTED',
  Join = 'JOIN',
  JoinSuccess = 'JOIN_SUCCESS',
  JoinFail = 'JOIN_FAIL',
  Leave = 'LEAVE',
  ForceLeave = 'FORCE_LEAVE',
  ServerLeaveGame = 'SERVER_LEAVE_GAME',
  NewSession = 'NEW_SESSION',
  SessionPhaseReceived = 'SESSION_PHASE_RECEIVED',
  BoxTurnReceived = 'BOX_TURN_RECEIVED',
  BoxPatchReceived = 'BOX_PATCH_RECEIVED',
  SessionPatchReceived = 'SESSION_PATCH_RECEIVED',
  GamePatchReceived = 'GAME_PATCH_RECEIVED',
  DepositReceived = 'DEPOSIT_RECEIVED',
  CardQueued = 'CARD_QUEUED',
  DebugStep = 'DEBUG_STEP',
  ActiveBoxRevealDone = 'ACTIVE_BOX_REVEAL_DONE',
}

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

export enum ERpService {
  JoinGameRequest = 'joinGameRequest',
  ListenGameEvents = 'listenGameEvents',
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export enum ERpAction {
  PatchStoreFromJoinResult = 'patchStoreFromJoinResult',
  NavigateLobby = 'navigateLobby',
  EmitLeaveGame = 'emitLeaveGame',
  EmitForceLeaveGame = 'emitForceLeaveGame',
  ClearError = 'clearError',
  SetJoinError = 'setJoinError',
  SetSessionId = 'setSessionId',
  SetBoxOnTurn = 'setBoxOnTurn',
  SetBoxOnTurnFromSnapshot = 'setBoxOnTurnFromSnapshot',
  EnqueueCard = 'enqueueCard',
  EnqueueBoxPatch = 'enqueueBoxPatch',
  ApplyNextCard = 'applyNextCard',
  DeferDecisionsPhase = 'deferDecisionsPhase',
  ClearDeferredPhaseAfterDealing = 'clearDeferredPhaseAfterDealing',
  ClearCardQueue = 'clearCardQueue',
  ApplySessionPatch = 'applySessionPatch',
  ApplyBoxPatch = 'applyBoxPatch',
  // Buffered patches — accumulated during dealing, applied atomically on decisions entry
  StoreDeferredSessionPatch = 'storeDeferredSessionPatch',
  ApplyDeferredSessionPatches = 'applyDeferredSessionPatches',
  ClearDeferredSessionPatches = 'clearDeferredSessionPatches',
  StoreDeferredBoxOnTurn = 'storeDeferredBoxOnTurn',
  FlushDeferredBoxOnTurn = 'flushDeferredBoxOnTurn',
  // Deferred BOX patches by seat — buffered during dealing, activated on decisions entry
  StoreDeferredBoxPatch = 'storeDeferredBoxPatch',
  ApplyAndClearDeferredBoxPatch = 'applyAndClearDeferredBoxPatch',
  ApplyGamePatch = 'applyGamePatch',
  ApplyDeposit = 'applyDeposit',
  ShowJoinFailToast = 'showJoinFailToast',
  ResetTransientContext = 'resetTransientContext',
}

// ---------------------------------------------------------------------------
// Payload types
// ---------------------------------------------------------------------------

export type BoxPatchPayload = {
  success: boolean
  target: IBoxCore
  data: Partial<TBoxBodyPublic>
  error?: { event: string; code: string; message: string }
}

export type SessionPatchPayload = {
  target: ISessionCore
  data: Partial<ISessionBodyPlayerPublic>
}

export type CardQueueItem =
  | { target: 'box'; data: BoxPatchPayload }
  | { target: 'dealer'; data: SessionPatchPayload }

export type RussianPokerJoinSuccessPayload = {
  success: boolean
  game: TGamePublic
  session: ISessionBodyPlayerPublic
  boxes: TBoxBodyPublic[]
  deposit: number
}

export type RussianPokerMachineEvent =
  | { type: ERpEvent.SocketConnected }
  | { type: ERpEvent.SocketDisconnected }
  | { type: ERpEvent.Join }
  | { type: ERpEvent.JoinSuccess; data: RussianPokerJoinSuccessPayload }
  | { type: ERpEvent.JoinFail; error: unknown }
  | { type: ERpEvent.Leave }
  | { type: ERpEvent.ForceLeave }
  | { type: ERpEvent.ServerLeaveGame }
  | { type: ERpEvent.NewSession; sessionId: string; gamePatch: any }
  | { type: ERpEvent.GamePatchReceived; gamePatch: any }
  | { type: ERpEvent.DepositReceived; depositData: any }
  // sessionPatch: raw socket data to be applied by ApplySessionPatch action.
  // Optional — reconnect-init events don't carry a patch (store already populated).
  | {
      type: ERpEvent.SessionPhaseReceived
      phase: ESessionPhase
      sessionPatch?: SessionPatchPayload
    }
  | {
      type: ERpEvent.BoxTurnReceived
      seatNumber: number | null
      sessionPatch?: SessionPatchPayload
    }
  // boxPatch: raw socket data applied by ApplyBoxPatch action inside the machine.
  | {
      type: ERpEvent.BoxPatchReceived
      seatNumber: number
      boxPatch: BoxPatchPayload
    }
  | { type: ERpEvent.SessionPatchReceived; sessionPatch: SessionPatchPayload }
  | { type: ERpEvent.CardQueued; item: CardQueueItem }
  | { type: ERpEvent.DebugStep }
  | { type: ERpEvent.ActiveBoxRevealDone }

// ---------------------------------------------------------------------------
// Runtime / context
// ---------------------------------------------------------------------------

export interface RussianPokerSocketApi {
  emit: (event: string, ...args: any[]) => void
  emitWithAck: (event: string, ...args: any[]) => Promise<any>
  on: (event: string, listener: (...args: any[]) => void) => void
  off: (event: string, listener?: (...args: any[]) => void) => void
}

export interface RussianPokerMachineRuntime {
  store: RussianPokerStore
  socket: RussianPokerSocketApi
  accessToken?: string
  navigateLobby: () => void
  // Returns true when the machine is in any dealing substate (idle or animating).
  // Use this instead of store.session.phase to avoid MobX/XState state mismatch.
  isInDealingPhase: () => boolean
}

export interface CreateRussianPokerMachineParams {
  gameId: string
  getRuntime: () => RussianPokerMachineRuntime
}

export interface RussianPokerMachineContext {
  gameId: string
  sessionId: string | null
  errorMessage: string | null
  boxOnTurn: number | null
  cardQueue: CardQueueItem[]
  // Typed phase deferred during dealing animation — null when not pending
  deferredPhaseAfterDealing: ESessionPhase | null
  // Session patches buffered during dealing — applied atomically on decisions entry
  deferredSessionPatches: SessionPatchPayload[]
  deferredBoxOnTurn: number | null
  // Real-card BOX patches by seat — buffered during dealing, consumed in decisions
  deferredBoxPatchesBySeat: Record<number, BoxPatchPayload>
}
